import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRefetchOnFocus } from '../../hooks/useRefetchOnFocus';
import { FilterRow } from '../../components/FilterRow';
import {
  ActivityIndicator,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { fpColors } from '../../styles/theme';
import { BrandLogo } from '../../components';
import { NotificationBell } from '../notifications/components/NotificationBell';
// FE #124.5 — Top-bar 🕒 clock-rewind icon (always visible).
import { ScanHistoryButton } from '../scans/components/ScanHistoryButton';
import { ShareFlowRow } from './components/ShareFlowRow';
import {
  loadUserShares,
  selectInboundClaimedShares,
  selectOutboundActiveShares,
} from './sharingSlice';
import {
  selectLentOutItems,
  loadInventoryItemsEnhanced,
} from '../browse/browseSlice';
import {
  loadReceivedRequests,
  loadSentRequests,
  selectPendingCount,
  selectReceivedRequests,
  selectSentRequests,
} from '../borrowRequests/borrowRequestsSlice';
import { BorrowRequestsEntryCard } from './components/BorrowRequestsEntryCard';

/**
 * ShareFlowScreen — Sprint 8 follow-up (PR4).
 *
 * The new "Share" tab. Answers a single question: where are my items
 * right now, across every transfer mechanism the app supports?
 *
 * Two sections, each unifying multiple data sources:
 *
 *   1. With me — items I'm holding that I don't own
 *      - Inbound CLAIMED `shared_with_me` (link-share claims)
 *      - [v2] Crew custody where I'm `current_custodian_user_id`
 *        but NOT `user_id` of the row (deferred — needs cross-crew
 *        inventory scan + cache hydration; PR5 picks this up)
 *
 *   2. Out from me — items I own/originated that someone else has
 *      - Outbound PENDING/CLAIMED `shared_by_me` (sent link-shares)
 *      - `selectLentOutItems` — items I own with custodian != me
 *        (crew handoffs)
 *      - My sent borrow requests in PENDING/APPROVED state
 *
 * Each row carries a source chip (`via link` / `via crew` /
 * `borrow ask`) so the user can disambiguate at a glance without
 * losing the unified view. Tap → ItemDetail when we have an item id;
 * otherwise no-op (PR5 wires the View-all detail surfaces that handle
 * the share-only rows).
 *
 * No "Needs your action" section — that lives in the notification
 * bell. Per the user's explicit call: avoid duplicating the inbox.
 */
export const ShareFlowScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();

  const inboundClaimed = useAppSelector(selectInboundClaimedShares);
  const outboundActive = useAppSelector(selectOutboundActiveShares);
  const lentOutCrew = useAppSelector(selectLentOutItems);
  const sentRequests = useAppSelector(selectSentRequests);
  // FE-254 — counts powering the new BorrowRequestsEntryCard.
  const receivedRequests = useAppSelector(selectReceivedRequests);
  const pendingReceived = useAppSelector(selectPendingCount);
  // Cast: slice types sentRequests as the mutation-response shape
  // (BorrowRequestResponse), but the runtime data is the list shape
  // (BorrowRequestDetails) which carries `status`. Mismatch pre-dates
  // this PR; fixing the slice type is a separate refactor.
  const pendingSent = useMemo(
    () =>
      sentRequests.filter((r) => (r as any).status === 'PENDING').length,
    [sentRequests],
  );

  useEffect(() => {
    (dispatch as any)(loadUserShares());
    // ensure the inventory cache is populated for the lent-out
    // selector (which derives from state.browse.inventoryItemsEnhanced)
    dispatch(loadInventoryItemsEnhanced({ refresh: false }) as any);
    try {
      (dispatch as any)(loadSentRequests());
    } catch {
      /* sent-requests endpoint may not exist yet; non-blocking */
    }
    // FE-254 — entry card needs received-pending count too.
    try {
      (dispatch as any)(loadReceivedRequests());
    } catch {
      /* non-blocking — selector returns 0 when state is empty */
    }
  }, [dispatch]);

  // FE-255 PR5 — refetch when the user comes back to this screen so
  // pending-borrow counts + recent shares reflect anything that
  // changed while they were on a sibling screen (deny / approve /
  // hand off from a notification or another tab).
  const refetchShareFlow = useCallback(() => {
    (dispatch as any)(loadUserShares());
    dispatch(loadInventoryItemsEnhanced({ refresh: true }) as any);
    try {
      (dispatch as any)(loadSentRequests());
    } catch {
      /* non-blocking */
    }
    try {
      (dispatch as any)(loadReceivedRequests());
    } catch {
      /* non-blocking */
    }
  }, [dispatch]);
  useRefetchOnFocus(refetchShareFlow);

  const openInbox = useCallback(
    () => navigation.navigate('Notifications'),
    [navigation],
  );
  const openItem = useCallback(
    (itemId: number | null | undefined) => {
      if (itemId == null) return;
      navigation.navigate('ItemDetail', { itemId });
    },
    [navigation],
  );

  // ── "With me" rows ────────────────────────────────────────────────
  const withMeRows = useMemo(() => {
    return inboundClaimed.map((s) => ({
      key: `link-in-${s.share_id}`,
      source: 'link' as const,
      itemName: s.item_name,
      contextLabel: `from ${stripEmail(s.shared_by)}`,
      timeLabel: formatRelativeShort(s.claimed_at ?? s.created_at),
      dueState: 'fine' as const,
      onPress: () => navigation.navigate('MyShares', { tab: 'received' }),
    }));
  }, [inboundClaimed, navigation]);

  // ── "Out from me" rows (3 unioned sources) ────────────────────────
  const outFromMeRows = useMemo(() => {
    const rows: {
      key: string;
      source: 'link' | 'crew' | 'borrow-ask';
      itemName: string;
      contextLabel: string;
      timeLabel: string;
      dueState: 'fine' | 'soon' | 'overdue';
      onPress?: () => void;
    }[] = [];

    // Outbound link shares (PENDING + CLAIMED)
    for (const s of outboundActive) {
      const ctx =
        s.status === 'CLAIMED' && s.shared_with
          ? `with ${stripEmail(s.shared_with)}`
          : s.shared_with
            ? `to ${stripEmail(s.shared_with)} · awaiting claim`
            : 'awaiting claim';
      rows.push({
        key: `link-out-${s.share_id}`,
        source: 'link',
        itemName: s.item_name,
        contextLabel: ctx,
        timeLabel: formatRelativeShort(s.claimed_at ?? s.created_at),
        dueState: 'fine',
        onPress: () => navigation.navigate('MyShares', { tab: 'sent' }),
      });
    }

    // Crew handoffs out (items I own, custodian != me)
    for (const it of lentOutCrew as any[]) {
      const dueIso = it.custody_due_date as string | null | undefined;
      const due = parseDueState(dueIso);
      rows.push({
        key: `crew-out-${it.id}`,
        source: 'crew',
        itemName: it.item_name ?? 'Untitled item',
        contextLabel:
          due.kind === 'overdue'
            ? `⚠ overdue ${due.label}`
            : due.kind === 'soon'
              ? `due ${due.label}`
              : `since ${formatRelativeShort(it.custody_since)}`,
        timeLabel:
          due.kind === 'overdue' || due.kind === 'soon'
            ? due.label
            : formatRelativeShort(it.custody_since),
        dueState: due.kind,
        onPress: () => openItem(it.id),
      });
    }

    // My outgoing borrow asks — pending / approved
    for (const r of sentRequests as any[]) {
      const status = String(r.status ?? '').toUpperCase();
      if (status !== 'PENDING' && status !== 'APPROVED') continue;
      rows.push({
        key: `borrow-${r.id ?? r.request_id}`,
        source: 'borrow-ask',
        itemName: r.item_name ?? 'Untitled item',
        contextLabel:
          status === 'APPROVED'
            ? 'Approved · ready to claim'
            : `Awaiting ${r.owner_name ?? 'owner'}`,
        timeLabel: formatRelativeShort(r.created_at),
        dueState: 'fine',
        onPress:
          r.item_id != null
            ? () => openItem(r.item_id)
            : undefined,
      });
    }

    return rows;
  }, [outboundActive, lentOutCrew, sentRequests, navigation, openItem]);

  const hasAnyRows = withMeRows.length > 0 || outFromMeRows.length > 0;
  const sharesLoading = false; // selector returns [] when unloaded
  const [refreshing, setRefreshing] = React.useState(false);
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await (dispatch as any)(loadUserShares());
      await dispatch(loadInventoryItemsEnhanced({ refresh: true }) as any);
      try {
        await (dispatch as any)(loadSentRequests());
      } catch {
        /* non-blocking */
      }
    } finally {
      setRefreshing(false);
    }
  }, [dispatch]);

  // Search-UX §3.7 — local filter chrome (search + source chips).
  // Linear's saved-view chip row + Apple Mail's filter pills.  All
  // filtering is client-side over the already-loaded set.
  const [shareSearch, setShareSearch] = useState('');
  const [shareSource, setShareSource] = useState<string>('all');

  const filterRow = (
    row: { source: string; itemName: string; contextLabel: string },
  ): boolean => {
    if (shareSource !== 'all' && row.source !== shareSource) return false;
    const q = shareSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      row.itemName.toLowerCase().includes(q) ||
      row.contextLabel.toLowerCase().includes(q)
    );
  };
  const filteredWithMe = withMeRows.filter(filterRow);
  const filteredOutFromMe = outFromMeRows.filter(filterRow);

  return (
    <View style={styles.page}>
      <View style={styles.topBar}>
        <BrandLogo variant="horizontal" height={24} tone="color" />
        <View style={styles.topRight}>
          {/* FE #124.5 — Scan history shortcut. */}
          <ScanHistoryButton
            onPress={() => (navigation as any).navigate('ScanHistory')}
          />
          <NotificationBell onPress={openInbox} />
        </View>
      </View>

      <ScrollView
        contentContainerStyle={styles.body}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            tintColor={fpColors.primary}
          />
        }
      >
        <View style={styles.hero}>
          <Text style={styles.heading}>Share</Text>
          <Text style={styles.subtitle}>
            Items moving between you and other people.
          </Text>
        </View>

        {/* FE-254 — persistent entry-point into BorrowRequestsManager.
            Replaces the notification-only path that left users stuck
            when they dismissed a borrow notification. */}
        <BorrowRequestsEntryCard
          pendingReceived={pendingReceived}
          pendingSent={pendingSent}
          totalHistorical={receivedRequests.length + sentRequests.length}
        />

        {/* Search-UX §3.7 — filter row: source chips + free-text.
            Hidden when there are no rows so the empty state stays
            clean.  Power users with 20+ active shares get the
            "just what I lent to Dylan" / "items I borrowed by
            name" path the audit calls out. */}
        {hasAnyRows && (
          <FilterRow
            query={shareSearch}
            onQueryChange={setShareSearch}
            placeholder="Filter by item or person"
            chips={[
              { key: 'all', label: 'All' },
              { key: 'link', label: 'Link' },
              { key: 'crew', label: 'Crew' },
              { key: 'borrow-ask', label: 'Borrow' },
            ]}
            activeKey={shareSource}
            onChipPress={setShareSource}
            testID="shareflow-filter"
          />
        )}

        {/* ── Borrowed (FE-205 rename — was "With me") ──────────── */}
        <SectionHeader
          icon="south-west"
          title="Borrowed"
          count={withMeRows.length}
          onViewAll={
            withMeRows.length > 3
              ? () => navigation.navigate('MyShares', { tab: 'received' })
              : undefined
          }
        />
        {withMeRows.length === 0 ? (
          <EmptyState
            icon="south-west"
            title="Nothing borrowed yet"
            body="When someone shares an item with you or hands one off in a circle, it'll show up here."
          />
        ) : filteredWithMe.length === 0 ? (
          <EmptyState
            icon="search-off"
            title="No matches"
            body="No borrowed items match the current filter."
          />
        ) : (
          <View style={styles.list}>
            {filteredWithMe.slice(0, 3).map((r) => {
              const { key, ...rowProps } = r;
              return <ShareFlowRow key={key} {...rowProps} />;
            })}
          </View>
        )}

        {/* ── Lent Out (FE-205 rename — was "Out from me") ──────── */}
        <SectionHeader
          icon="north-east"
          title="Lent Out"
          count={outFromMeRows.length}
          onViewAll={
            outFromMeRows.length > 3
              ? () => navigation.navigate('MyShares', { tab: 'sent' })
              : undefined
          }
        />
        {outFromMeRows.length === 0 ? (
          <EmptyState
            icon="north-east"
            title="Nothing out from you"
            body="Share an item via link, hand one off in a circle, or ask to borrow — those will all show up here."
          />
        ) : filteredOutFromMe.length === 0 ? (
          <EmptyState
            icon="search-off"
            title="No matches"
            body="No outgoing items match the current filter."
          />
        ) : (
          <View style={styles.list}>
            {filteredOutFromMe.slice(0, 5).map((r) => {
              const { key, ...rowProps } = r;
              return <ShareFlowRow key={key} {...rowProps} />;
            })}
          </View>
        )}

        {!hasAnyRows && sharesLoading && (
          <View style={styles.loadingWrap}>
            <ActivityIndicator color={fpColors.primary} />
          </View>
        )}
      </ScrollView>
    </View>
  );
};

/* ──────────────────────────────────────────────────────────────────
 * Small inline components
 * ────────────────────────────────────────────────────────────────── */

const SectionHeader: React.FC<{
  icon: keyof typeof MaterialIcons.glyphMap;
  title: string;
  count: number;
  onViewAll?: () => void;
}> = ({ icon, title, count, onViewAll }) => (
  <View style={styles.sectionHeader}>
    <View style={styles.sectionHeaderLeft}>
      <MaterialIcons name={icon} size={16} color={fpColors.primary} />
      <Text style={styles.sectionTitle}>{title}</Text>
      {count > 0 && (
        <View style={styles.countPill}>
          <Text style={styles.countPillText}>
            {count > 99 ? '99+' : String(count)}
          </Text>
        </View>
      )}
    </View>
    {onViewAll && (
      <Pressable onPress={onViewAll} hitSlop={8} accessibilityRole="button">
        <Text style={styles.viewAll}>View all →</Text>
      </Pressable>
    )}
  </View>
);

const EmptyState: React.FC<{
  icon: keyof typeof MaterialIcons.glyphMap;
  title: string;
  body: string;
}> = ({ icon, title, body }) => (
  <View style={styles.emptyState}>
    <MaterialIcons name={icon} size={28} color={fpColors.outline} />
    <Text style={styles.emptyTitle}>{title}</Text>
    <Text style={styles.emptyBody}>{body}</Text>
  </View>
);

/* ──────────────────────────────────────────────────────────────────
 * Pure helpers — exported for unit tests later if needed
 * ────────────────────────────────────────────────────────────────── */

/** Strips the "(email@x.com)" suffix from a "Name (email)" string. */
function stripEmail(raw: string | null | undefined): string {
  if (!raw) return 'someone';
  const namePart = raw.split('(')[0].trim();
  return namePart || 'someone';
}

/** Compact relative timestamp — "1h", "just now", "Apr 28". */
function formatRelativeShort(iso: string | null | undefined): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const diffSec = (Date.now() - t) / 1000;
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h`;
  if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)}d`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

/** Classify a due date as fine / soon (within 24h) / overdue. */
function parseDueState(
  iso: string | null | undefined,
): { kind: 'fine' | 'soon' | 'overdue'; label: string } {
  if (!iso) return { kind: 'fine', label: '' };
  const dueMs = new Date(iso).getTime();
  if (!Number.isFinite(dueMs)) return { kind: 'fine', label: '' };
  const diffMs = dueMs - Date.now();
  const oneDay = 86400_000;
  if (diffMs < 0) {
    const days = Math.ceil(Math.abs(diffMs) / oneDay);
    return { kind: 'overdue', label: `${days}d` };
  }
  if (diffMs <= oneDay) {
    return { kind: 'soon', label: 'today' };
  }
  return {
    kind: 'fine',
    label: new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    }),
  };
}

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: fpColors.background },
  topBar: {
    height: 56,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: fpColors.surfaceContainerLowest,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
  },
  topRight: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  body: { padding: 16, gap: 12, paddingBottom: 96 },
  hero: { gap: 4 },
  heading: {
    fontFamily: 'Inter_800ExtraBold',
    fontSize: 26,
    color: fpColors.onSurface,
    letterSpacing: -0.3,
  },
  subtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
    lineHeight: 18,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 14,
    marginBottom: 6,
  },
  sectionHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  sectionTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  countPill: {
    minWidth: 22,
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: fpColors.primaryContainer,
    alignItems: 'center',
  },
  countPillText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.primary,
  },
  viewAll: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 12,
    color: fpColors.primary,
  },
  list: {
    backgroundColor: fpColors.surface,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    overflow: 'hidden',
  },
  emptyState: {
    paddingVertical: 22,
    paddingHorizontal: 16,
    alignItems: 'center',
    gap: 6,
    backgroundColor: fpColors.surfaceContainerLow,
    borderRadius: 14,
  },
  emptyTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onSurface,
  },
  emptyBody: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
    lineHeight: 17,
    maxWidth: 320,
  },
  loadingWrap: { padding: 24, alignItems: 'center' },
});

export default ShareFlowScreen;
